import { BRAND_NAME } from '../brand'

/**
 * 注册协议 —— 用户注册前必须阅读并勾选。
 *
 * 为什么单独成文件、用结构化数据而不是一大段字符串：
 * 协议要在页面上以可读的模态框展示（标题 / 章节 / 条款分级），而且现在就有
 * 两份（注册协议、公开分享声明），将来还可能加隐私政策。结构化后每份协议
 * 是一份 `LegalDoc`，渲染逻辑复用一份，改文案不用碰 UI。
 *
 * ⚠️ 品牌名一律用 `BRAND_NAME`（漫漫弹）而不是写死 —— 站名是单一事实来源，
 *    见 brand.ts 的说明。协议正文里凡「[你的产品名称]」都用它替换。
 */

export type LegalSection = {
	/** 章节标题，如「一、服务说明」 */
	heading: string
	/** 条款数组；每条是一段文字 */
	items: string[]
}

export type LegalDoc = {
	id: string
	title: string
	/** 协议版本号 —— 注册时连同勾选一起写进 user_metadata，作为「同意的是哪一版」的凭证 */
	version: string
	/** 生效日期（展示用） */
	effectiveDate: string
	/** 引言段（标题下、第一章前） */
	intro: string[]
	/** 结尾确认段 */
	closing: string
	sections: LegalSection[]
}

/**
 * 文档一：用户注册协议（注册时勾选）。
 *
 * 2026-10-28：生效日期按定稿填成 2026 年 10 月 1 日（原先是待填的空位）。
 * `version` 保持 `1.0` —— 它是写进 `auth_user.agreement_version` 的凭证值，
 * 改文案不算改版本，**只有条款实质变化才升版本**，否则老记录的版本号会对不上。
 */
export const USER_AGREEMENT: LegalDoc = {
	id: 'user-agreement',
	title: `${BRAND_NAME} 用户服务协议`,
	version: '1.0',
	effectiveDate: '2026 年 10 月 1 日',
	intro: [
		`欢迎使用 ${BRAND_NAME}（以下简称“本平台”）。请你在注册前仔细阅读本协议全部内容，特别是以粗体标注的免责条款、责任限制条款。你勾选“我已阅读并同意”并完成注册，即视为你已充分理解并接受本协议全部内容。`,
	],
	closing:
		'本人已仔细阅读并充分理解上述全部条款，特别是加粗标注的免责与责任限制条款，同意接受本协议约束。',
	sections: [
		{
			heading: '一、服务说明',
			items: [
				'1.1 本平台是一款面向音乐爱好者的简谱在线编辑、试听与跟谱练习工具（以下简称“本服务”）。',
				'1.2 本平台提供的核心功能包括：简谱编辑、试听播放、跟谱练习、练习记录等。本平台不主动提供、不主动制作、不主动上传任何受著作权保护的曲谱内容。',
				'1.3 本平台有权根据运营需要调整、暂停或终止部分或全部服务，并将提前以站内公告等方式通知。',
			],
		},
		{
			heading: '二、账号注册与使用',
			items: [
				'2.1 你应保证注册时提供的全部信息真实、准确、完整。',
				'2.2 你应妥善保管账号及密码，因你保管不善导致的损失由你自行承担。',
				'2.3 你不得将账号转让、出借、出租或以其他方式提供给第三方使用。',
			],
		},
		{
			heading: '三、用户上传与分享规则（核心条款）',
			items: [
				'3.1 本平台允许用户创作并保存自己的简谱作品。用户可选择将作品设为“私密”或“公开”。',
				'3.2 用户选择“公开”分享其曲谱作品时，必须同时勾选并同意《曲谱公开分享免责声明》，否则无法完成公开操作。',
				'3.3 用户保证：其上传或公开的任何曲谱内容，均为其本人独立创作，或已取得原曲著作权人的合法授权，不侵犯任何第三方的著作权、商标权或其他合法权益。',
				'3.4 用户不得上传或公开以下内容：\n（一）未经授权对流行歌曲、影视配乐、商业音乐等受著作权保护作品进行的扒谱、改编或整理；\n（二）从其他平台、网站、软件搬运、复制、下载后重新上传的曲谱文件；\n（三）任何侵犯他人著作权、邻接权或其他合法权利的内容；\n（四）违反法律法规或公序良俗的内容。',
				'3.5 用户对其上传、公开的全部内容独立承担全部法律责任。因用户上传、公开内容引发的任何争议、投诉、索赔、诉讼，均由用户自行处理并承担全部后果，与本平台无关。',
			],
		},
		{
			heading: '四、平台的权利与义务',
			items: [
				'4.1 本平台为信息存储与工具服务平台，不具备专业版权审查能力，不对用户上传内容的合法性、真实性、准确性作实质性审查，亦不构成对内容合法性的保证。',
				'4.2 本平台有权对用户上传内容进行形式审核，并有权对认为不适当的内容进行删除、屏蔽或修改。',
				'4.3 本平台收到权利人有效侵权通知后，将依法及时采取删除、屏蔽、断开链接等必要措施。',
				'4.4 本平台有权对多次上传侵权内容的用户采取警告、限制功能、封禁账号等措施。',
			],
		},
		{
			heading: '五、知识产权',
			items: [
				'5.1 本平台的软件、界面、代码、设计等知识产权归本平台或相关权利人所有。',
				'5.2 用户在本平台创作的作品，著作权归用户本人所有。用户选择公开分享的，视为授权本平台在平台范围内展示、存储、传播该作品。',
			],
		},
		{
			heading: '六、免责声明',
			items: [
				'6.1 本平台仅提供工具与信息存储服务，不对用户上传内容的合法性负责。',
				'6.2 因用户违反本协议或相关法律法规，导致本平台遭受任何损失（包括但不限于赔偿、罚款、律师费、诉讼费），用户应全额赔偿。',
				'6.3 本平台不对因不可抗力、网络故障、第三方原因导致的服务中断或数据丢失承担责任。',
			],
		},
		{
			heading: '七、协议修改与终止',
			items: [
				'7.1 本平台有权适时修改本协议，修改后的协议将在平台公示。',
				'7.2 你继续使用本服务即视为接受修改后的协议。如不同意，应停止使用并注销账号。',
			],
		},
		{
			heading: '八、法律适用与争议解决',
			items: [
				'8.1 本协议适用中华人民共和国法律。',
				'8.2 因本协议产生的争议，双方应友好协商解决；协商不成的，提交本平台运营主体所在地有管辖权的人民法院诉讼解决。',
			],
		},
	],
}

/**
 * 注册时把「同意了哪版协议」写进 user_metadata 的载荷。
 *
 * 为什么留痕：一旦发生纠纷，需要证明"用户注册时同意的是这一版"。版本号 +
 * 时间戳就够，不存 IP 等敏感信息（也未必拿得到）。
 */
export function agreementAcceptancePayload(doc: LegalDoc) {
	return {
		agreed_terms_id: doc.id,
		agreed_terms_version: doc.version,
		agreed_terms_at: new Date().toISOString(),
	}
}

/**
 * 文档二：曲谱公开分享免责声明。
 *
 * 与《用户服务协议》是两份独立的 LegalDoc，但**在注册页同一颗勾选框里一起
 * 同意**（`AuthPage` 的协议行）：注册是"建号"这一个动作，两份文档分别管
 * 账号本身与以后公开作品的责任，都在那一步表态。分成两颗会让人以为可以
 * 只同意其中一份 —— 而只同意账号协议的人，把谱公开到广场时仍是没授权的。
 * 各自可点开看全文（同一套 `LegalDocModal`）。
 *
 * 于是「公开到广场」不再有任何确认框：同意在注册时已经取得，每次公开都
 * 甩一份长文让人重读，结果只能是闭眼勾。
 *
 * 版本独立编号：这份声明可能比主协议更频繁地调整（公开规则的细化），
 * 分开版本号便于分别留痕 —— 注册时两份版本合成 `1.0+1.0` 写进
 * `auth_user.agreement_version`（只有一列，见 models.User 的注释）。
 */
export const SHARE_DISCLAIMER: LegalDoc = {
	id: 'share-disclaimer',
	title: '曲谱公开分享免责声明',
	version: '1.0',
	effectiveDate: '2026 年 10 月 1 日',
	intro: [
		'请你务必仔细阅读本声明。你勾选“我同意”并完成公开分享操作，即视为你已充分理解并接受本声明全部内容。',
	],
	closing:
		'本人已仔细阅读并充分理解上述全部内容，自愿作出上述声明与保证，并同意承担相应法律责任。',
	sections: [
		{
			heading: '一、声明目的',
			items: [
				`本声明用于明确：当你将自己在 ${BRAND_NAME}（以下简称“本平台”）中创作的曲谱设置为“公开”时，你与该曲谱相关的版权责任归属。`,
			],
		},
		{
			heading: '二、你的声明与保证',
			items: [
				'你在此郑重声明并保证：',
				'你公开分享的曲谱，系你本人独立创作完成，你对该曲谱享有完整、合法的著作权。',
				'你公开分享的曲谱，不包含任何未经授权使用的第三方音乐作品内容，包括但不限于：\n（一）未经授权对流行歌曲、影视配乐、商业音乐等进行的扒谱、改编或整理；\n（二）未经授权复制、搬运、修改他人已发表的曲谱作品；\n（三）任何侵犯他人著作权、邻接权或其他合法权益的内容。',
				'你公开分享曲谱的行为，不违反任何法律法规，不侵犯任何第三方的合法权益。',
			],
		},
		{
			heading: '三、责任承担',
			items: [
				'你理解并同意：你对所公开分享曲谱的合法性独立承担全部责任。',
				'如因你公开分享的曲谱侵犯第三方著作权或其他权利，引发任何投诉、举报、索赔、诉讼或行政处罚，均由你本人独立处理并承担全部法律责任及经济赔偿，与本平台无关。',
				'如本平台因你的公开分享行为遭受任何损失（包括但不限于赔偿金、罚款、律师费、诉讼费、商誉损失），你应全额赔偿本平台。',
			],
		},
		{
			heading: '四、平台声明',
			items: [
				'本平台是简谱编辑与练习工具，并非专业版权审查机构，不具备对用户上传内容进行专业版权审查的能力和义务。',
				'本平台对用户公开分享的曲谱内容，不进行实质性版权审查，亦不对其合法性作出任何明示或默示的保证。',
				'本平台收到权利人有效侵权通知后，将依法及时下架相关内容。你理解并同意，平台的下架行为不构成对你行为合法性的认定，亦不免除你应承担的法律责任。',
			],
		},
		{
			heading: '五、其他',
			items: [
				`本声明是你与 ${BRAND_NAME} 之间关于曲谱公开分享事项的补充约定，与你注册时同意的《用户服务协议》具有同等效力。`,
				'本声明适用中华人民共和国法律。',
			],
		},
	],
}

